import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../hooks/useAuth";
import { detectSubdomain } from "../utils/subdomain";

interface MenuItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  permission: string;
  role?: string;
}

// SVG Icon Components
const DashboardIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
    />
  </svg>
);

const PaymentsIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const PromptPayIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1v-2a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
    />
  </svg>
);

const EcommerceIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
    />
  </svg>
);

const LocationIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const InventoryIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    />
  </svg>
);

const TenantsIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const AnalyticsIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const ReportsIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const SettingsIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

const SecurityIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const WalletIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
    />
  </svg>
);

const AffiliateIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const MonitorIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

const OrdersIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
    />
  </svg>
);

const ProductsIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    />
  </svg>
);

const POSIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const SupportIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
    />
  </svg>
);

const AuditIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  </svg>
);

const ApiIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
    />
  </svg>
);

const RevolutionaryIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 10V3L4 14h7v7l9-11h-7z"
    />
  </svg>
);

const Sidebar: React.FC<{
  isMobileOpen?: boolean;
  setIsMobileOpen?: (open: boolean) => void;
}> = ({ isMobileOpen, setIsMobileOpen }) => {
  const { user, logout, hasPermission, hasRole } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const [currentSubdomain, setCurrentSubdomain] = useState<string | null>(null);

  useEffect(() => {
    const subdomain = detectSubdomain();
    setCurrentSubdomain(subdomain);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const menuSections: { title: string; items: MenuItem[] }[] = [
    {
      title: "การชำระเงิน",
      items: [
        {
          path: "/dashboard",
          label: "แดชบอร์ด",
          icon: <DashboardIcon />,
          permission: "dashboard",
        },
        {
          path: "/promptpay",
          label: "PromptPay",
          icon: <PromptPayIcon />,
          permission: "payments",
        },
        {
          path: "/payments",
          label: "ประวัติการชำระ",
          icon: <PaymentsIcon />,
          permission: "payments",
        },
        {
          path: "/wallet",
          label: "Wallet",
          icon: <WalletIcon />,
          permission: "dashboard",
        },
        {
          path: "/location-tracking",
          label: "Location Tracking",
          icon: <LocationIcon />,
          permission: "dashboard",
        },
      ],
    },
    {
      title: "ธุรกิจออนไลน์",
      items: [
        {
          path: "/ecommerce",
          label: "ร้านค้า",
          icon: <EcommerceIcon />,
          permission: "ecommerce",
        },
        {
          path: "/pos",
          label: "จุดขาย (POS)",
          icon: <POSIcon />,
          permission: "ecommerce",
          role: "merchant",
        },
        {
          path: "/orders",
          label: "คำสั่งซื้อ",
          icon: <OrdersIcon />,
          permission: "ecommerce",
          role: "merchant",
        },
        {
          path: "/products",
          label: "สินค้า",
          icon: <ProductsIcon />,
          permission: "ecommerce",
          role: "merchant",
        },
        {
          path: "/inventory",
          label: "สต็อกสินค้า",
          icon: <InventoryIcon />,
          permission: "inventory",
        },
      ],
    },
    {
      title: "การตลาด",
      items: [
        {
          path: "/affiliate",
          label: "พาร์ทเนอร์",
          icon: <AffiliateIcon />,
          permission: "dashboard",
        },
        {
          path: "/campaigns",
          label: "แคมเปญ",
          icon: <RevolutionaryIcon />,
          permission: "marketing",
        },
      ],
    },
    {
      title: "การจัดการ",
      items: [
        {
          path: "/analytics",
          label: "วิเคราะห์ข้อมูล",
          icon: <AnalyticsIcon />,
          permission: "analytics",
        },
        {
          path: "/reports",
          label: "รายงาน",
          icon: <ReportsIcon />,
          permission: "reports",
        },
        {
          path: "/tenants",
          label: "ผู้ใช้งาน",
          icon: <TenantsIcon />,
          permission: "tenants",
        },
        {
          path: "/settings",
          label: "ตั้งค่า",
          icon: <SettingsIcon />,
          permission: "settings",
        },
      ],
    },
    {
      title: "บริการลูกค้า",
      items: [
        {
          path: "/support",
          label: "ศูนย์ช่วยเหลือ",
          icon: <SupportIcon />,
          permission: "support",
          role: "support",
        },
      ],
    },
    {
      title: "ผู้ดูแลระบบ",
      items: [
        {
          path: "/monitor",
          label: "ตรวจสอบระบบ",
          icon: <MonitorIcon />,
          permission: "all",
          role: "superadmin",
        },
        {
          path: "/security",
          label: "ความปลอดภัย",
          icon: <SecurityIcon />,
          permission: "security",
          role: "superadmin",
        },
        {
          path: "/audit",
          label: "บันทึกกิจกรรม",
          icon: <AuditIcon />,
          permission: "support",
          role: "superadmin",
        },
        {
          path: "/api",
          label: "จัดการ API",
          icon: <ApiIcon />,
          permission: "all",
          role: "superadmin",
        },
      ],
    },
  ];

  // Filter menu items based on user permissions, role requirements, and subdomain
  const filteredMenuSections = menuSections
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => {
          // Check permission
          if (item.permission && !hasPermission(item.permission)) return false;
          // Check role requirement
          if (item.role && !hasRole(item.role as any)) return false;
          // Check subdomain match (if subdomain is detected, only show items for that role)
          if (currentSubdomain && item.role && currentSubdomain !== item.role)
            return false;
          return true;
        })
        .filter((item, index, self) =>
          // Remove duplicates based on path
          index === self.findIndex((t) => t.path === item.path)
        ),
    }))
    .filter((section) => section.items.length > 0);

  return (
    <>
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes slideOut {
          from { transform: translateX(0); }
          to { transform: translateX(-100%); }
        }
      `}</style>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity duration-300"
          onClick={() => setIsMobileOpen?.(false)}
        />
      )}
      <div
        className={`text-white transition-all duration-300 w-56 ${
          isMobileOpen
            ? "fixed inset-y-0 left-0 z-50 md:sticky md:top-0 animate-[slideIn_0.3s_ease-out]"
            : "fixed left-0 top-0 md:sticky md:top-0 -translate-x-full md:translate-x-0"
        } md:flex min-h-screen flex flex-col neon-border backdrop-filter backdrop-blur-xl`}
        style={{
          background: "linear-gradient(135deg, rgba(12, 74, 110, 0.9) 0%, rgba(0, 0, 0, 0.9) 50%, rgba(12, 74, 110, 0.9) 100%)",
          boxShadow: "0 0 30px rgba(12, 74, 110, 0.2), 0 0 60px rgba(255, 255, 255, 0.1), inset 0 0 30px rgba(12, 74, 110, 0.05)",
        }}
      >
      {/* Navigation Menu */}
      <nav className="flex-1 p-3 space-y-4">
        {filteredMenuSections.map((section) => (
          <div key={section.title}>
            <h3
              className="text-[10px] font-semibold text-white/80 uppercase tracking-wider mb-2 block neon-text"
              style={{
                textShadow: "0 0 5px rgba(255, 255, 255, 0.6), 0 0 10px rgba(30, 58, 138, 0.4)",
              }}
            >
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setIsMobileOpen && setIsMobileOpen(false)}
                      className={`flex items-center p-3 md:p-2 rounded-lg transition-all duration-200 relative overflow-hidden group active:scale-95 ${
                        isActive(item.path)
                          ? "bg-gradient-to-r from-navy-blue/20 to-white/20 text-white shadow-lg shadow-white/20 border border-white/40 neon-border"
                          : "text-white/60 hover:bg-white/10 hover:text-white hover:border hover:border-white/30 hover:shadow-lg hover:shadow-white/10"
                      }`}
                      title=""
                    >
                      {/* Sacred geometry glow effect */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-r from-navy-blue/10 via-white/10 to-green/10 blur-md ${isActive(item.path) ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity duration-300`}
                        style={{
                          background: "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(30, 58, 138, 0.05) 10px, rgba(30, 58, 138, 0.05) 20px)",
                        }}
                      ></div>
                      <span className="relative flex-shrink-0">
                        {item.icon}
                      </span>
                      <span className="relative ml-2 md:ml-2 text-sm md:text-sm font-medium">
                        {item.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-3 border-t border-white/10">
          <div className="mb-2 block">
            <div className="text-xs font-medium text-white/80">
              {user?.name || "Admin"}
            </div>
            <div className="text-[10px] text-white/60">
              {user?.email || "admin@comex.com"}
            </div>
          </div>
          <button
            onClick={logout}
            className={`w-full flex items-center justify-center p-2 bg-gradient-to-r from-red-600/80 to-red-800/80 hover:from-red-500 hover:to-red-700 rounded-lg transition-all duration-300 text-white text-sm font-medium border border-red-400/30 hover:border-red-400/50 shadow-lg shadow-red-400/20 hover:shadow-xl hover:shadow-red-400/30 neon-border px-3`}
            title=""
          >
            <LogoutIcon />
            <span className="ml-2">{t("logout")}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
